import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Upload, FileSpreadsheet, Loader2, Check, AlertTriangle, ArrowRight, ArrowLeft, Package, Users, Download
} from 'lucide-react';
import { toast } from 'sonner';

const ImportPage = () => {
  const { API, isSuperAdmin } = useAuth();
  const [step, setStep] = useState(1);
  const [tipo, setTipo] = useState('prodotti');

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = `${API}/import/template/${tipo}`;
    link.download = `template_import_${tipo}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [chiese, setChiese] = useState([]);
  const [selectedChiesa, setSelectedChiesa] = useState('');
  const [result, setResult] = useState(null);

  const targetFields = tipo === 'prodotti'
    ? [
      { key: 'nome_prodotto', label: 'Nome Prodotto', required: true },
      { key: 'categoria', label: 'Categoria' },
      { key: 'quantita_disponibile', label: 'Quantita' },
      { key: 'valore_unitario', label: 'Valore Unitario' },
      { key: 'unita_misura', label: 'Unita di Misura' },
    ] : [
      { key: 'nome_referente', label: 'Nome Referente', required: true },
      { key: 'telefono', label: 'Telefono' },
      { key: 'indirizzo', label: 'Indirizzo' },
      { key: 'comune', label: 'Comune' },
      { key: 'numero_adulti', label: 'Adulti' },
      { key: 'bambini_0_3', label: 'Bambini 0-3' },
      { key: 'bambini_4_5', label: 'Bambini 4-5' },
      { key: 'bambini_6_12', label: 'Bambini 6-12' },
      { key: 'ragazzi_13_17', label: 'Ragazzi 13-17' },
      { key: 'anziani', label: 'Anziani' },
      { key: 'isee', label: 'ISEE' },
      { key: 'scadenza_isee', label: 'Scadenza ISEE' },
      { key: 'codice_famiglia', label: 'Codice Famiglia' },
      { key: 'email', label: 'Email' },
    ];

  const handleFileUpload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/import/preview?tipo=${tipo}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreviewData(res.data);
      setMapping(res.data.ai_mapping || {});

      if (isSuperAdmin()) {
        const chieseRes = await axios.get(`${API}/chiese`);
        setChiese(chieseRes.data);
      }

      setStep(2);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore nella lettura del file'));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(mapping));
      if (selectedChiesa) formData.append('chiesa_id', selectedChiesa);

      const endpoint = tipo === 'prodotti' ? 'import/prodotti' : 'import/famiglie';
      const res = await axios.post(`${API}/${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      setStep(3);
      toast.success(`${res.data.imported} ${tipo} importati con successo!`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Errore durante l'importazione"));
    } finally {
      setLoading(false);
    }
  };

  const updateMapping = (sourceCol, targetField) => {
    const newMapping = { ...mapping };
    if (targetField === '__none__') {
      delete newMapping[sourceCol];
    } else {
      newMapping[sourceCol] = targetField;
    }
    setMapping(newMapping);
  };

  const reset = () => {
    setStep(1);
    setFile(null);
    setPreviewData(null);
    setMapping({});
    setResult(null);
    setSelectedChiesa('');
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="import-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-bold">Import Massivo</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">Importa prodotti o famiglie da file Excel o CSV</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 sm:gap-4">
        {[
          { n: 1, label: 'Carica File' },
          { n: 2, label: 'Mappa Colonne' },
          { n: 3, label: 'Risultato' },
        ].map((s) => (
          <div key={s.n} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
              ${step >= s.n ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              {step > s.n ? <Check className="w-4 h-4" /> : s.n}
            </div>
            <span className={`text-xs sm:text-sm hidden sm:inline ${step >= s.n ? 'font-medium' : 'text-muted-foreground'}`}>{s.label}</span>
            {s.n < 3 && <ArrowRight className="w-4 h-4 text-muted-foreground" />}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Upload className="w-5 h-5" /> Carica File
            </CardTitle>
            <CardDescription>Seleziona il tipo di dati e carica un file Excel (.xlsx) o CSV</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo di Import</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setTipo('prodotti')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${tipo === 'prodotti' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                  data-testid="import-tipo-prodotti"
                >
                  <Package className="w-6 h-6 mb-2 text-primary" />
                  <p className="font-medium text-sm">Prodotti</p>
                  <p className="text-xs text-muted-foreground">Magazzino</p>
                </button>
                <button
                  onClick={() => setTipo('famiglie')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${tipo === 'famiglie' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                  data-testid="import-tipo-famiglie"
                >
                  <Users className="w-6 h-6 mb-2 text-primary" />
                  <p className="font-medium text-sm">Famiglie</p>
                  <p className="text-xs text-muted-foreground">Anagrafica</p>
                </button>
              </div>
            </div>

            {/* Download Template */}
            <div className="p-4 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">Scarica File Esempio</p>
                  <p className="text-xs text-muted-foreground">
                    File Excel precompilato con esempi e istruzioni per {tipo === 'prodotti' ? 'i prodotti' : 'le famiglie'}. Compila e ricarica.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadTemplate}
                  className="flex-shrink-0 border-primary text-primary hover:bg-primary hover:text-white"
                  data-testid="download-template-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Template {tipo === 'prodotti' ? 'Prodotti' : 'Famiglie'}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>File (Excel o CSV)</Label>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setFile(e.target.files[0])}
                data-testid="import-file-input"
              />
              {file && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileSpreadsheet className="w-4 h-4" />
                  <span>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                </div>
              )}
            </div>

            <Button onClick={handleFileUpload} disabled={!file || loading} className="w-full" data-testid="import-preview-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Anteprima e Mappa Colonne
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Mapping */}
      {step === 2 && previewData && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Mappatura Colonne (AI)</CardTitle>
              <CardDescription>
                L'AI ha suggerito la mappatura. Verifica e correggi se necessario.
                {previewData.total_rows} righe trovate.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {isSuperAdmin() && (
                <div className="space-y-2 p-3 bg-muted rounded-lg">
                  <Label>Assegna a Chiesa *</Label>
                  <Select value={selectedChiesa} onValueChange={setSelectedChiesa}>
                    <SelectTrigger data-testid="import-chiesa-select"><SelectValue placeholder="Seleziona chiesa" /></SelectTrigger>
                    <SelectContent>
                      {chiese.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {previewData.columns.map((col) => (
                <div key={col} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{col}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      es: {previewData.preview[0]?.[col] || '-'}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="w-40 sm:w-48 flex-shrink-0">
                    <Select value={mapping[col] || '__none__'} onValueChange={(v) => updateMapping(col, v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- Ignora --</SelectItem>
                        {targetFields.map(f => (
                          <SelectItem key={f.key} value={f.key}>{f.label}{f.required ? ' *' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Preview Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Anteprima (prime 5 righe)</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {previewData.columns.map(col => (
                      <TableHead key={col} className="text-xs whitespace-nowrap">{col}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.preview.map((row, i) => (
                    <TableRow key={i}>
                      {previewData.columns.map(col => (
                        <TableCell key={col} className="text-xs whitespace-nowrap">{row[col] || '-'}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="import-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
            </Button>
            <Button onClick={handleImport} disabled={loading || (isSuperAdmin() && !selectedChiesa)} className="flex-1" data-testid="import-confirm-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Importa {previewData.total_rows} {tipo}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Result */}
      {step === 3 && result && (
        <Card>
          <CardContent className="p-6 sm:p-8 text-center space-y-4">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${result.errors.length === 0 ? 'bg-green-100' : 'bg-amber-100'}`}>
              {result.errors.length === 0 ? (
                <Check className="w-8 h-8 text-green-600" />
              ) : (
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold">Importazione completata</h2>
              <p className="text-sm text-muted-foreground mt-1">
                <Badge variant="default" className="mr-2">{result.imported}</Badge> {tipo} importati su {result.total} totali
              </p>
            </div>
            {result.errors.length > 0 && (
              <div className="text-left bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 max-h-40 overflow-y-auto">
                <p className="text-xs font-medium text-amber-700 mb-1">Errori:</p>
                {result.errors.map((e, i) => (
                  <p key={i} className="text-xs text-amber-600">{e}</p>
                ))}
              </div>
            )}
            <Button onClick={reset} className="w-full sm:w-auto" data-testid="import-reset-btn">
              Importa altri dati
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ImportPage;
